import { useState, useMemo } from 'react'
import { RotateCcw } from 'lucide-react'
import {
  useAgentConfigs,
  usePipelineConfigs,
  useResetAllAgentConfigs,
  useResetAllPipelineConfigs,
  useTaskReactFlow,
  useLastCompletedTaskId,
} from '../hooks/useAIConfig'
import { AgentConfigDrawer } from '../components/ai-config/AgentConfigDrawer'
import { StrategyCanvas } from '../components/ai-config/canvas/StrategyCanvas'
import { ViewToggle } from '../components/ai-config/ViewToggle'
import type { ViewMode } from '../components/ai-config/ViewToggle'
import { PipelineView } from '../components/ai-config/editorial/PipelineView'
import { ExecutionSummaryPanel, deriveTeamHealthFromTrace } from '../components/ai-config/editorial/ExecutionSummaryPanel'
import { BrandVoicePanel } from '../components/ai-config/editorial/BrandVoicePanel'
import type { TeamHealth } from '../components/ai-config/editorial/types'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { QueryErrorState } from '../components/ui/QueryErrorState'

export function AIConfigPage() {
  const [showGlobalReset, setShowGlobalReset] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('editorial')
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const lastTaskId = useLastCompletedTaskId()

  const agentsQuery = useAgentConfigs()
  const pipelinesQuery = usePipelineConfigs()
  const traceQuery = useTaskReactFlow(lastTaskId)
  const resetAllAgents = useResetAllAgentConfigs()
  const resetAllPipelines = useResetAllPipelineConfigs()

  const agents = agentsQuery.data ?? []

  const isLoading = agentsQuery.isLoading || pipelinesQuery.isLoading
  const isError = agentsQuery.isError || pipelinesQuery.isError

  const handleGlobalReset = async () => {
    setShowGlobalReset(false)
    await resetAllAgents.mutateAsync()
    await resetAllPipelines.mutateAsync()
  }

  const teamHealth = useMemo(
    () =>
      traceQuery.data
        ? deriveTeamHealthFromTrace(traceQuery.data)
        : ({ content: 'idle', visual: 'idle', strategy: 'idle' } as Record<string, TeamHealth>),
    [traceQuery.data]
  )

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Configuracion IA</h1>
          <p className="text-slate-500 mt-1">Configura agentes, modelos y pipelines del sistema</p>
        </div>
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Configuracion IA</h1>
          <p className="text-slate-500 mt-1">Configura agentes, modelos y pipelines del sistema</p>
        </div>
        <QueryErrorState
          title="Error al cargar la configuracion"
          error={agentsQuery.error || pipelinesQuery.error}
          onRetry={() => {
            agentsQuery.refetch()
            pipelinesQuery.refetch()
          }}
          isRefetching={agentsQuery.isRefetching || pipelinesQuery.isRefetching}
        />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Configuracion IA</h1>
          <p className="text-slate-500 mt-1">Configura agentes, modelos y pipelines del sistema</p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <ViewToggle mode={viewMode} onChange={setViewMode} />

          {showGlobalReset ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Restaurar TODO a defaults?</span>
              <button
                type="button"
                onClick={handleGlobalReset}
                disabled={resetAllAgents.isPending || resetAllPipelines.isPending}
                className="btn text-sm px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
              >
                {resetAllAgents.isPending || resetAllPipelines.isPending ? 'Restaurando...' : 'Confirmar'}
              </button>
              <button
                type="button"
                onClick={() => setShowGlobalReset(false)}
                className="btn text-sm px-3 py-1.5 bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowGlobalReset(true)}
              className="btn btn-secondary inline-flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Restaurar Todo
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden border-t border-slate-200">
        {viewMode === 'editorial' && (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl space-y-6">
              <div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Pipeline de Generacion
                </h2>
                <PipelineView
                  agents={agents}
                  teamHealth={teamHealth}
                  onAgentSelect={setSelectedAgentId}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                    Ultima Ejecucion
                  </h2>
                  <ExecutionSummaryPanel
                    trace={traceQuery.data}
                    isLoading={traceQuery.isLoading}
                    onViewTechnical={() => setViewMode('technical')}
                  />
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                    Voz de Marca
                  </h2>
                  <BrandVoicePanel
                    agents={agents}
                    onAgentSelect={setSelectedAgentId}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'technical' && (
          <div className="flex-1 overflow-hidden">
            <StrategyCanvas
              agents={agents}
              trace={traceQuery.data}
              isTraceLoading={traceQuery.isLoading}
              onAgentSelect={setSelectedAgentId}
            />
          </div>
        )}
      </div>

      <AgentConfigDrawer
        agentId={selectedAgentId}
        agents={agents}
        onClose={() => setSelectedAgentId(null)}
      />
    </div>
  )
}
